import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { NotificationService } from '../../services/notification-service/notification.service';

/**
 * Global Error Handler
 *
 * Catches all uncaught errors in the application (both HTTP and client-side errors).
 * Provides consistent error handling and user feedback across the entire application.
 *
 * Features:
 * - Catches uncaught exceptions
 * - Differentiates between HTTP and client-side errors
 * - Shows user-friendly notifications
 * - Logs errors to console for debugging
 *
 * Usage:
 * Automatically registered in CoreModule as the global ErrorHandler.
 * No manual integration needed - works automatically for all errors.
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  constructor(private injector: Injector) {}

  handleError(error: Error | HttpErrorResponse): void {
    const notificationService = this.injector.get(NotificationService);

    if (error instanceof HttpErrorResponse) {
      // HTTP errors are primarily handled by HttpErrorInterceptor
      // This is a fallback for any HTTP errors that slip through
      this.handleHttpError(error, notificationService);
    } else {
      // Client-side errors (JavaScript errors, etc.)
      this.handleClientError(error, notificationService);
    }

    // Always log to console for debugging
    console.error('Global Error Handler:', error);
  }

  private handleHttpError(error: HttpErrorResponse, notificationService: NotificationService): void {
    // Check if user is offline
    if (!navigator.onLine) {
      notificationService.showError('ERROR.NO_CONNECTION', 'Keine Internetverbindung');
    } else {
      // Show error message from backend or fallback
      const message = error.error?.langKey || 'ERROR.UNKNOWN';
      notificationService.showError(message);
    }
  }

  private handleClientError(error: Error, notificationService: NotificationService): void {
    // In development: Show detailed error
    // In production: Show generic error message
    if (!this.isProduction()) {
      notificationService.showError(undefined, `Client Error: ${error.message}`);
    } else {
      notificationService.showError('ERROR.APPLICATION_ERROR', 'Ein Fehler ist aufgetreten');
    }
  }

  private isProduction(): boolean {
    // Simple production check based on hostname
    // You can enhance this with proper environment detection
    return window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
  }
}
